import { Box, Flex, Heading, Icon, Table, TableContainer, Tbody, Td, Text, Tr } from "@chakra-ui/react"
import { benchmarkLevels, extendedCalculateKpi } from "@esgt/method-structure"
import { calculateFormula } from "@esgt/method-structure/src/kpi/calculateKPIFormula"
import { getCategoryKpiIds } from "@esgt/types"
import { MethodI18nFragment } from "components/I18nText"
import * as d3 from "d3"
import { formatKpiValue, getKpiUnitLabel } from "lib/helpers/formatKpiValue"
import { formatValue } from "lib/helpers/formatValue"
import { useMethod } from "lib/hooks"
import { getLanguage } from "lib/i18n/utils"
import { useRating } from "lib/providers/RatingProvider"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import styled from "styled-components"
import { Page, PreviewWatermark, SectionHeading } from "../components"

import { useD3 } from "./useD3"

const Container = styled.div`
	break-after: page;
	position: relative;
`

const textSize = 16

const computeLeafLabel = (d: any) => {
  let rotation = 0
  let anchor = "start"
  let dy = 0

  if (d.x0 > Math.PI) {
    rotation = (((d.x0 + d.x1) / 2) * 180) / Math.PI + 90
    anchor = "end"
    dy = -(d.x1 - d.x0) * textSize * Math.PI - 10
  } else {
    rotation = (((d.x0 + d.x1) / 2) * 180) / Math.PI - 90
    anchor = "start"
    dy = (d.x1 - d.x0) * textSize * Math.PI + 35
  }

  return { rotation, anchor, dy }
}

const computeCategoryLabel = (d: any) => {
  let rotation = 0
  let anchor = "start"
  let dy = 0

  if (d.x0 > Math.PI) {
    rotation = (((d.x0 + d.x1) / 2) * 180) / Math.PI + 90
    anchor = "end"
    dy = -(d.x1 - d.x0) * textSize * Math.PI - 10
  } else {
    rotation = (((d.x0 + d.x1) / 2) * 180) / Math.PI - 90
    anchor = "start"
    dy = (d.x1 - d.x0) * textSize * Math.PI + 19
  }

  return { rotation, anchor, dy }
}

export const SunburstChart: React.FC = () => {
  const rating = useRating()
  const { ratingState, ratingProfileConfig, isReady, ratingProfile, isFree } = rating
  // For free ratings the method returned by useRating is the reduced method version
  const freeMethod = isFree ? rating.method : null
  // Therefore we need to get the full method manually here for free ratings to display the entire sunchart
  const method = isFree ? useMethod(ratingProfile?.revisionOrActiveRevision?.methodId)?.method : rating.method

  const { i18n } = useTranslation()

  const calcData = useMemo(
    () =>
      isReady && ratingProfile && method
        ? method.config.kpiDimensions.map((dimension) => {
            const categories = dimension.categories.map((category) => {
              const kpis = getCategoryKpiIds(category)
                .map((id) => method.kpisById[id])
                .filter((kpi) => {
                  return !(ratingProfileConfig?.kpis?.[kpi.id] && ratingProfileConfig?.kpis?.[kpi.id].enabled === false)
                })
                .map((kpi) => {
                  const calc = extendedCalculateKpi(
                    kpi,
                    ratingProfileConfig,
                    ratingState.datapoints,
                    ratingState.anomalies,
                    ratingState.phase,
                    ratingState.overriddenKpisById,
                    method.kpisById,
                  )
                  const greyedOut = isFree && !freeMethod?.kpisById[kpi.id]
                  return {
                    ...kpi,
                    calculations: {
                      ...calc,
                      color: greyedOut
                        ? "#C5C5C5"
                        : calc.noDataCause === "no_benchmark"
                          ? "#8ab4e0"
                          : calc.noDataCause === "no_data"
                            ? "#b04f4f"
                            : calc.color,
                    },
                  }
                })

              const sum = kpis
                .filter((k) => !!k.calculations.benchmarkLevel)
                .map((k) => k.calculations.benchmarkLevel)
                .reduce((prev, curr) => prev + curr, 0)

              const numOfCountingKpis = kpis.filter((k) => k.calculations.noDataCause !== "no_benchmark").length
              const avg = numOfCountingKpis ? sum / numOfCountingKpis : 0
              const greyedOut = isFree && kpis.some((kpi) => !freeMethod?.kpisById[kpi.id])
              return {
                ...category,
                color: greyedOut ? "#C6C6C6" : benchmarkLevels[Math.round(avg)].color,
                avg,
                kpis,
                greyedOut,
              }
            })

            const numOfCountingCategories = categories.length
            const sum = categories.map((k) => k.avg).reduce((prev, curr) => prev + curr, 0)

            const avg = numOfCountingCategories ? sum / numOfCountingCategories : 0
            const greyedOut = isFree && categories.some((cat) => cat.greyedOut)
            return {
              ...dimension,
              categories,
              color: greyedOut ? "#C6C6C6" : benchmarkLevels[Math.round(avg)].color,
            }
          })
        : [],
    [isReady, ratingProfile, method],
  )

  const ref = useD3(() => {
    if (!isReady) return
    if (!ratingProfile) return
    if (!method) return
    const vWidth = 2000
    const vHeight = 1000
    const vRadius = Math.min(vWidth, vHeight) / 2

    const vData = {
      id: "TOPICS",
      children: calcData.map((dimension) => ({
        id: `dimension_${dimension.id}`,
        label: getLanguage(dimension.name, i18n),
        color: dimension.color,
        children: dimension.categories.map((category) => ({
          id: `category_${category.id}`,
          label: getLanguage(category.name, i18n),
          color: category.color,
          children: category.kpis.map((kpi) => {
            return {
              id: `kpi_${kpi.id}`,
              type: "label",
              label: getLanguage(kpi.name, i18n),
              size: 1,
              color: kpi.calculations.color,
            }
          }),
        })),
      })),
    }

    // Prepare our physical space
    const g = d3
      .select("svg")
      .attr("width", vWidth)
      .attr("height", vHeight)
      .append("g")
      .attr("transform", `translate(${vWidth / 2}, ${vHeight / 2})`)

    // Declare d3 layout
    const vLayout = d3.partition().size([2 * Math.PI, vRadius])
    const ringHeights = [0.1, 0.2, 0.6, 0.1]
    const vArc = d3
      .arc<any>()
      .startAngle((d) => d.x0)
      .endAngle((d) => d.x1)
      .innerRadius((d) => ringHeights.slice(0, d.depth).reduce((sum, n) => sum + n * vRadius, 0))
      .outerRadius((d) => ringHeights.slice(0, d.depth + 1).reduce((sum, n) => sum + n * vRadius, 0))

    // Layout + Data
    const vRoot = d3.hierarchy<any>(vData).sum((d) => d.size)
    const vNodes = vRoot.descendants()
    vLayout(vRoot)
    const vSlices = g.selectAll("g").data(vNodes).enter().append("path")

    // Dimension labels
    g.selectAll("g")
      .data(vNodes.filter((d) => d.depth === 1))
      .enter()
      .append("text")
      .attr("transform", (d) => `translate(${vArc.centroid(d)})`) // <-- 3
      .attr("text-anchor", "middle")
      .attr("dx", "0")
      .attr("dy", "0")
      .style("fill", "#ffffff")
      .style("font-weight", "600")
      .style("font-size", "18px")
      .text((d) => d.data.label.toUpperCase())

    // Category labels
    g.selectAll("g")
      .data<any>(vNodes.filter((d) => d.depth === 2))
      .enter()
      .append("text")
      .attr("text-anchor", "middle")
      // .attr('dy', d => computeCategoryLabel(d).dy)
      .style("fill", "#ffffff")
      .style("font-weight", "600")
      .style("font-size", "18px")
      .attr(
        "transform",
        (d) => {
          const { rotation } = computeCategoryLabel(d)
          return `translate(${vArc.centroid(d)[0]}, ${vArc.centroid(d)[1] + textSize / 2}) rotate(${rotation})`
        },
        //
      )
      .text((d) => d.data.label)

    // KPI labels
    g.selectAll("g")
      .data<any>(vNodes.filter((d) => d.data.type === "label"))
      .enter()
      .append("text")
      .attr("text-anchor", (d) => computeLeafLabel(d).anchor)
      .attr("dy", (d) => computeLeafLabel(d).dy)
      .style("fill", (d) => d.data.color)
      .style("font-weight", "600")
      .attr(
        "transform",
        (d) => {
          const { rotation } = computeLeafLabel(d)
          return `translate(
						${(vRadius + 15) * Math.sin(d.x0)},
						${-(vRadius + 15) * Math.cos(d.x0)}
					) rotate(${rotation})`
        },
        //
      )
      .text((d) => d.data.label)

    // Draw on screen
    vSlices
      .filter((d) => !!d.parent)
      .attr("d", vArc)
      .style("stroke", "#fff")
      .style("stroke-width", "3")
      .style("fill", (d) => d.data.color)
  }, [isReady, ratingProfile, method])

  const selectedKpis = useMemo(() => {
    if (calcData.length === 0 || !calcData[1]) return

    const calculated = calcData[1]

    const circularEconomyCategory = calculated.categories[1]
    const emissionsCategory = calculated.categories[2]

    const recycledWaste = circularEconomyCategory?.kpis.find((kpi) => kpi.kpiNumber === "2.20.100") || null
    const scope1 = emissionsCategory?.kpis.find((e) => e.kpiNumber === "2.30.100") || null
    const scope2Location = emissionsCategory?.kpis.find((e) => e.kpiNumber === "2.30.500") || null
    const scope2Market = emissionsCategory?.kpis.find((e) => e.kpiNumber === "2.30.600") || null

    return {
      recycledWaste,
      scope1,
      scope2Location,
      scope2Market,
    }
  }, [calcData])

  const resourceTotals = useMemo(() => {
    const data = ratingState.datapoints

    const fossilFuelConsumption = [data.literBensin, data.literDiesel, data.literOlje].some((val) => val === undefined)
      ? undefined
      : calculateFormula("literBensin + literDiesel + literOlje", {
          literBensin: data.literBensin ?? 0,
          literDiesel: data.literDiesel ?? 0,
          literOlje: data.literOlje ?? 0,
        })

    const electricityConsumption = [
      data.kwhPeriodeArealEiet,
      data.kwhPeriodeArealLeiet,
      data.kwhPeriodeArealUtleid,
      data.egenProdusertKwhIkkeFornybar,
      data.egenProdusertKwhFornybar,
      data.egenProdusertkwhVidereSolgt,
    ].some((val) => val === undefined)
      ? undefined
      : calculateFormula(
          "kwhPeriodeArealEiet + kwhPeriodeArealLeiet - kwhPeriodeArealUtleid + egenProdusertKwhIkkeFornybar + egenProdusertKwhFornybar - egenProdusertkwhVidereSolgt",
          {
            kwhPeriodeArealEiet: data.kwhPeriodeArealEiet ?? 0,
            kwhPeriodeArealLeiet: data.kwhPeriodeArealLeiet ?? 0,
            kwhPeriodeArealUtleid: data.kwhPeriodeArealUtleid ?? 0,
            egenProdusertKwhIkkeFornybar: data.egenProdusertKwhIkkeFornybar ?? 0,
            egenProdusertKwhFornybar: data.egenProdusertKwhFornybar ?? 0,
            egenProdusertkwhVidereSolgt: data.egenProdusertkwhVidereSolgt ?? 0,
          },
        )

    const waterConsumption = [data.m3vannPeriodeEiet, data.m3vannPeriodeLeiet, data.m3vannPeriodeUtleid].some(
      (val) => val === undefined,
    )
      ? undefined
      : calculateFormula("m3vannPeriodeEiet + m3vannPeriodeLeiet - m3vannPeriodeUtleid", {
          m3vannPeriodeEiet: data.m3vannPeriodeEiet ?? 0,
          m3vannPeriodeLeiet: data.m3vannPeriodeLeiet ?? 0,
          m3vannPeriodeUtleid: data.m3vannPeriodeUtleid ?? 0,
        })

    const kgAvfall = [data.kgAvfallPeriodeEiet, data.kgAvfallPeriodeLeiet, data.kgAvfallPeriodeUtleid].some(
      (val) => val === undefined,
    )
      ? undefined
      : calculateFormula("kgAvfallPeriodeEiet + kgAvfallPeriodeLeiet - kgAvfallPeriodeUtleid", {
          kgAvfallPeriodeEiet: data.kgAvfallPeriodeEiet ?? 0,
          kgAvfallPeriodeLeiet: data.kgAvfallPeriodeLeiet ?? 0,
          kgAvfallPeriodeUtleid: data.kgAvfallPeriodeUtleid ?? 0,
        })

    return {
      fossilFuelConsumption,
      electricityConsumption,
      waterConsumption,
      kgAvfall,
    }
  }, [ratingState])

  const TableText = ({ children, ...rest }) => (
    <Text whiteSpace={"break-spaces"} maxWidth="100%" margin={0} fontSize={"10pt"} {...rest}>
      {children}
    </Text>
  )

  const Resources = () => (
    <ResourcesContainer>
      <SectionHeading>Kritiske ressurser</SectionHeading>
      <TableContainer>
        <Table width={"100%"} variant="simple">
          <Tbody>
            <Tr>
              <Td padding={0}>
                <TableText>Forbruk fossilt drivstoff liter</TableText>
              </Td>
              <Td padding={0}>
                <TableText>
                  {resourceTotals.fossilFuelConsumption !== undefined
                    ? formatValue(resourceTotals.fossilFuelConsumption.toString())
                    : " —  "}
                  {" liter"}
                </TableText>
              </Td>
            </Tr>
            <Tr>
              <Td padding={0}>
                <TableText>Forbruk strøm i kwh</TableText>
              </Td>
              <Td padding={0}>
                <TableText>
                  {resourceTotals.electricityConsumption !== undefined
                    ? formatValue(resourceTotals.electricityConsumption.toString())
                    : " —  "}
                  {" kWh"}
                </TableText>
              </Td>
            </Tr>
            <Tr>
              <Td padding={0}>
                <TableText>Forbruk vann i m3</TableText>
              </Td>
              <Td padding={0}>
                <TableText>
                  {resourceTotals.waterConsumption !== undefined
                    ? formatValue(resourceTotals.waterConsumption.toString())
                    : " —  "}
                  {" m3"}
                </TableText>
              </Td>
            </Tr>
            <Tr>
              <Td padding={0}>
                <TableText>Avfall i kg</TableText>
              </Td>
              <Td padding={0}>
                <TableText>
                  {resourceTotals.kgAvfall !== undefined ? formatValue(resourceTotals.kgAvfall.toString()) : " —  "}
                  {" kg"}
                </TableText>
              </Td>
            </Tr>
            <Tr>
              <Td padding={0}>
                <TableText>Resirkulert avfall</TableText>
              </Td>
              <Td padding={0}>
                <TableText>
                  {selectedKpis?.recycledWaste?.calculations?.value !== undefined
                    ? formatKpiValue(
                        selectedKpis.recycledWaste.calculations.value.toString(),
                        selectedKpis.recycledWaste.unit,
                      )
                    : " —  "}{" "}
                  {selectedKpis?.recycledWaste?.unit &&
                    getKpiUnitLabel(selectedKpis.recycledWaste.unit, ratingState?.answerValues)}
                </TableText>
              </Td>
            </Tr>
            <Tr>
              <Td padding={0}>
                <TableText>Scope 1 - Direkte utslipp</TableText>
              </Td>
              <Td padding={0}>
                <TableText>
                  {selectedKpis?.scope1?.calculations?.value !== undefined
                    ? formatKpiValue(selectedKpis.scope1.calculations.value.toString(), selectedKpis.scope1.unit)
                    : " —  "}{" "}
                  {selectedKpis?.scope1?.unit && getKpiUnitLabel(selectedKpis.scope1.unit, ratingState?.answerValues)}
                </TableText>
              </Td>
            </Tr>
            <Tr>
              <Td padding={0}>
                <TableText>Scope 2 - Lokasjonsbasert</TableText>
              </Td>
              <Td padding={0}>
                <TableText>
                  {selectedKpis?.scope2Location?.calculations?.value !== undefined
                    ? formatKpiValue(
                        selectedKpis.scope2Location.calculations.value.toString(),
                        selectedKpis.scope2Location.unit,
                      )
                    : " —  "}{" "}
                  {selectedKpis?.scope2Location?.unit &&
                    getKpiUnitLabel(selectedKpis.scope2Location.unit, ratingState?.answerValues)}
                </TableText>
              </Td>
            </Tr>
            <Tr>
              <Td padding={0}>
                <TableText>Scope 2 - Markedsbasert</TableText>
              </Td>
              <Td padding={0}>
                <TableText>
                  {selectedKpis?.scope2Market?.calculations?.value !== undefined
                    ? formatKpiValue(
                        selectedKpis.scope2Market.calculations.value.toString(),
                        selectedKpis.scope2Market.unit,
                      )
                    : " —  "}{" "}
                  {selectedKpis?.scope2Market?.unit &&
                    getKpiUnitLabel(selectedKpis.scope2Market.unit, ratingState?.answerValues)}
                </TableText>
              </Td>
            </Tr>
          </Tbody>
        </Table>
      </TableContainer>
    </ResourcesContainer>
  )

  const Benchmarks = () => (
    <>
      <BenchmarksContainer>
        {[...benchmarkLevels].reverse().map((bml, i) => (
          <BenchmarkLevel key={i}>
            <Flex flexFlow="row nowrap" alignItems="center" gap="6px">
              <CircleIcon color={bml.color} />
              <Box fontWeight="400">{bml.name}</Box>
            </Flex>
          </BenchmarkLevel>
        ))}
        <BenchmarkLevel>
          <Flex flexFlow="row nowrap" alignItems="center" gap="6px">
            {isFree ? (
              <>
                <CircleIcon color="#C5C5C5" />
                <Box fontWeight="400">Ikke inkludert i gratis versjon</Box>
              </>
            ) : (
              <>
                <CircleIcon color={"#7EACDA"} />
                <Box fontWeight="400">Ingen benchmark</Box>
              </>
            )}
          </Flex>
        </BenchmarkLevel>
      </BenchmarksContainer>
      <Text m="4mm 16mm 0">
        Resultatene presenteres i prosent, tall, forholdstall eller vurdering fra 0 - 100, hvor 100 er Meget god. Ved
        manglende data, vil resultatet automatisk plasseres under meget svak.
      </Text>
    </>
  )

  return (
    <>
      <Container data-page="legacy">
        <PreviewWatermark />
        <SectionHeading margin="0 16mm">RESULTAT</SectionHeading>
        {isFree && (
          <Heading margin="1mm 16mm 0" fontWeight={300} fontSize={"13pt"}>
            GRATIS -{" "}
            <Text as="span" fontWeight={600}>
              IKKE VERTIFISERT OG UFULLSTENDIG BÆREKRAFTSRAPPORT
            </Text>
          </Heading>
        )}

        <svg
          ref={ref}
          viewBox="0 0 2000 1000"
          style={{
            height: "200mm",
            width: "100%",
          }}
        />
        <Benchmarks />
      </Container>
      <Page named="legacy">
        <Resources />
      </Page>
    </>
  )
}

const CircleIcon = (props: any) => (
  <Icon viewBox="0 0 100 100" {...props} width="16px" height="16px">
    <circle cx="50" cy="50" r="30" fill="currentColor" />
    {props.outline && <circle cx="50" cy="50" r="45" fill="none" strokeWidth={7} stroke="currentColor" />}
  </Icon>
)

const ResourcesContainer = styled.div`
  line-height: 14pt;
  font-size: 10pt;
  font-weight: 100;
  position: relative;
`

const BenchmarksContainer = styled.div`
  margin: 0 16mm;
	background-color: rgb(253, 253, 253);
	border: 1px solid rgb(223, 223, 219);
	border-radius: 8px;
	padding: 12px;
	display: grid;
	grid-template-columns: 1fr 1fr 1fr;
	gap: 8px;
	font-size: 14px;
`

const BenchmarkLevel = styled.div`
	display: flex;
	flex-flow: column nowrap;
	gap: 1px;
`
